import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { UIRouterGlobals } from '@uirouter/core';
import { DisplayApiService } from 'src/app/api/services/display-api.service';
import { ScreenSharingApiService } from 'src/app/api/services/screen-sharing-api.service';
import { DISPLAY_ACCESS } from 'src/app/auth/types/access-level';
import { ActionsMenu } from 'src/app/components/common-actions/common-actions.component';
import {
  AccessControlList,
  ShareWithUsersModalComponent,
} from 'src/app/components/share-with-users-modal/share-with-users-modal.component';
import { compareIgnoringFields } from 'src/app/shared/helpers/object.helper';
import { BaseEditableComponent } from '../../../shared/components/base-editable/base-editable.component';
import { Display } from '../../../api/services/display';
import { DisplayControlService } from '../../services/display-control.service';
import { DisplayService } from '../../services/display.service';
import { PlayerActionsService } from '../../services/player-actions.service';
import { PlayerProService } from '../../services/player-pro.service';
import { ScreenshotService } from '../../services/screenshot.service';
import { DisplayFieldsComponent } from '../display-fields/display-fields.component';
import { DisplayStatusComponent } from '../display-status/display-status.component';

@Component({
  selector: 'display-details',
  templateUrl: './display-details.component.html',
  styleUrls: ['./display-details.component.scss'],
  standalone: false,
})
export class DisplayDetailsComponent
  extends BaseEditableComponent<Display>
  implements OnInit, OnDestroy
{
  selectedSchedule: any = null;

  displayControlType: string = 'hdmi-cec';

  @ViewChild('displayForm') displayForm: NgForm;
  @ViewChild(DisplayFieldsComponent) displayFields: DisplayFieldsComponent;
  actions: ActionsMenu = {};
  displayStatusComponent = DisplayStatusComponent;

  constructor(
    public displayService: DisplayService,
    public displayControlService: DisplayControlService,
    private screenshotService: ScreenshotService,
    private uiRouterGlobals: UIRouterGlobals,
    private playerActionsService: PlayerActionsService,
    private playerProService: PlayerProService,
    private screenSharingApiService: ScreenSharingApiService,
    private displayApiService: DisplayApiService,
  ) {
    super();
  }

  ngOnInit(): void {
    const displayId = this.uiRouterGlobals.params.displayId;
    this.displayService.init();

    this.displayService.getDisplay(displayId).then(async () => {
      const asyncOperations = [
        this.displayControlService
          .isDisplayControlEnabled(displayId)
          .then(
            (enabled) =>
              (this.displayService.display.isDisplayControlEnabled = enabled),
          ),
        this.displayControlService
          .getInterfaceType(this.displayService.display.id)
          .then((type) => (this.displayControlType = type)),
      ];

      if (this.displayService.hasSchedule(this.displayService.display)) {
        this.selectedSchedule = {
          id: this.displayService.display.scheduleId,
          name: this.displayService.display.scheduleName,
          companyId: this.displayService.display.companyId,
        };
      }

      if (!this.displayService.display.playerProAuthorized) {
        this.displayService.display.monitoringEnabled = false;
      }

      Promise.all(asyncOperations).finally(() => {
        this.oldObject = this.cloneDeep(this.displayService.display);
      });

      // Notify display-fields component that display data has been loaded
      // Use setTimeout to ensure ViewChild is available
      setTimeout(() => {
        if (this.displayFields) {
          this.displayFields.onDisplayDataLoaded();
        }
      });

      if (this.displayService.display.onlineStatus === 'online') {
        this.screenshotService.requestScreenshot();
      } else {
        this.screenshotService.loadScreenshot();
      }

      this.actions = {
        saveAction: {
          id: 'saveButton',
          label: 'Save',
          disabled:
            this.displayForm.invalid || this.displayService.savingDisplay,
          requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
        },
        cancelAction: {
          id: 'cancelButton',
          label: 'Cancel',
          action: () => {
            this.bypassUnsavedChanges();
            this.stateService.go('apps.displays.list');
          },
        },
        deleteAction: {
          id: 'deleteButton',
          label: 'Delete Display',
          icon: 'trash',
          action: () => this.confirmDelete(),
          disabled: !this.displayService.display.id,
          requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
        },
        moreOptions: [
          {
            id: 'shareDisplayButton',
            label: 'Share with others',
            icon: 'add-user',
            action: () => this.shareWithOthers(),
            requireRole: '!PROD',
          },
          {
            id: 'installDisplayButton',
            label: 'Install Display',
            icon: 'download',
            action: () => this.stateService.go('apps.displays.install'),
          },
          {
            id: 'updateFirmwareButton',
            label: 'Update Firmware',
            icon: 'update',
            action: () =>
              this.playerActionsService.confirm(
                this.displayService.display.id,
                this.displayService.display.name,
                PlayerActionsService.ACTION_TYPES.UPDATE_FIRMWARE,
              ),
            disabled:
              this.displayService.display.onlineStatus === 'not_activated' ||
              !this.playerProService.isFirmwareUpdateCompatiblePlayer(
                this.displayService.display,
              ) ||
              !this.displayService.display.playerVersion,
            requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
          },
          {
            id: 'updateOTAButton',
            label: 'OTA Update',
            icon: 'update',
            action: () =>
              this.playerActionsService.confirm(
                this.displayService.display.id,
                this.displayService.display.name,
                PlayerActionsService.ACTION_TYPES.OTA_UPDATE,
              ),
            disabled:
              this.displayService.display.onlineStatus === 'not_activated' ||
              this.displayService.display.onlineStatus === 'offline' ||
              !this.playerProService.isOTAUpdateCompatiblePlayer(
                this.displayService.display,
              ) ||
              !this.displayService.display.playerVersion,
            requireRole: 'sa',
          },
        ],
      };
    });
  }

  ngOnDestroy(): void {
    this.displayService.revertLicenseChangesIfNeeded();
  }

  async shareWithOthers(): Promise<void> {
    try {
      const displayId = this.displayService.display.id;
      const accessControlList =
        this.displayApiService.getAccessControlList(displayId);
      await this.modalService.showMediumDialog(
        ShareWithUsersModalComponent,
        {
          accessControlList,
          accessLevels: ['Screen Sharing Moderator', 'Full Access'],
          defaultAccessLevel: 'Screen Sharing Moderator',
          saveAccessControlList: async (
            updatedAccessControlList: AccessControlList,
          ) => {
            await this.displayApiService.updateAccessControlList(displayId, {
              ...(await accessControlList),
              ...(updatedAccessControlList as NonNullable<
                Display['accessControlList']
              >),
            });
          },
        },
        { width: '640px', maxWidth: 'calc(100vw - 32px)' },
      );
    } catch (error) {
      if (error instanceof Error && !error.message) {
        return;
      }

      console.warn('Failed to share display', error);
      await this.modalService.showMessage(
        'Unable to share display',
        'There was an error updating access to this display. Please try again.',
      );
    }
  }

  canDeactivate(): boolean {
    if (!this.oldObject || !this.displayService.display) {
      return true;
    }
    // Fields to ignore during comparison (system-updated fields)
    const fieldsToIgnore = [
      'onlineStatus',
      'playerVersion',
      'originalPlayerProAuthorized',
      'originalInteractiveAuthorized',
      'overrideSchedules',
      'overrideScheduleNames',
    ];

    return compareIgnoringFields(
      this.oldObject,
      this.displayService.display,
      fieldsToIgnore,
    );
  }

  async save() {
    if (!this.displayForm.valid) {
      console.info('form not valid: ', this.displayForm.errors);
      return Promise.reject();
    }

    const screenSharingTurnedOff =
      this.oldObject?.screenSharingEnabled &&
      !this.displayService.display.screenSharingEnabled;

    try {
      await this.displayService.updateDisplay(this.selectedSchedule);

      if (screenSharingTurnedOff) {
        try {
          await this.screenSharingApiService.deleteSession(
            this.displayService.display.id,
          );
        } catch (err) {
          console.warn('Failed to delete screen sharing session', err);
        }
      }
      this.oldObject = this.cloneDeep(this.displayService.display);
    } catch (err) {
      console.warn('Failed to update display', err);
      throw err;
    }
  }

  confirmDelete() {
    this.modalService
      .confirmDanger(
        'Delete Display?',
        'Are you sure you want to delete this Display?',
        'Yes',
        'No',
      )
      .then(async () => {
        this.bypassUnsavedChanges();
        try {
          await this.displayService.deleteDisplay();
        } catch (e) {
          this.resetUnsavedChanges();
        }
      });
  }
}
